import { api, formatError } from '../api-client'
import { analyticsEvent, flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { levenshtein } from './context/flags'
import { resolveWorkspaceSlug } from './context'

// Member CLI — owner/admin workspace operations (invite, role, remove). CLI-only
// (no dashboard parity needed). Reads + writes are direct API calls; the server
// gates role-changing ops to owner/admin, so the CLI just relays the verdict.

// MEMBER_ROLES lives in ./help-text alongside MEMBER_HELP (which interpolates it),
// re-exported here so it still resolves from './member' for validation + callers.
export { MEMBER_ROLES } from './help-text'
import { MEMBER_ROLES } from './help-text'
// Invitations can only grant admin or member — never owner.
export const INVITE_ROLES = ['admin', 'member']

const KNOWN_MEMBER_FLAGS: ReadonlySet<string> = new Set(['role', 'json', 'help'])

export function assertKnownMemberFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_MEMBER_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null
    let bestDist = Infinity
    for (const known of KNOWN_MEMBER_FLAGS) {
      const d = levenshtein(f, known)
      if (d < bestDist) { bestDist = d; best = known }
    }
    const hint = best !== null && bestDist <= Math.max(2, Math.floor(f.length / 2)) ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless member':\n` +
      lines.join('\n') +
      `\nRun 'driftless member --help' for valid flags.`,
  )
}

// Help lives in ./help-text (light, dep-free) so index.ts can print it without
// importing this command body; re-exported here so MEMBER_HELP still resolves from './member'.
export { MEMBER_HELP } from './help-text'
import { MEMBER_HELP } from './help-text'

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(MEMBER_HELP)
}

export async function memberCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help') { printHelp(); process.exit(0) }
  if (sub === undefined) { printHelp(process.stderr); process.exit(1) }
  assertKnownMemberFlags(flags)

  const ws = await resolveWorkspaceSlug()
  const base = `/workspaces/${ws}`

  try {
    switch (sub) {
      case 'list': {
        const result = (await api.get(`${base}/members`)) as any[]
        if (isJSON) { emitJSON(result); break }
        if (!Array.isArray(result) || !result.length) { console.log('No members.'); break }
        for (const m of result) {
          const role = (m.role || '?').toString().padEnd(8)
          const who = m.email ?? m.name ?? m.clerk_id ?? ''
          console.log(`${m.id}  ${role} ${who}`)
        }
        break
      }
      case 'invite': {
        const email = positional[1]
        if (!email) { console.error('Error: an email is required. Usage: driftless member invite <email> [--role admin|member]'); process.exit(1) }
        const body: Record<string, unknown> = { email }
        if (flags['role'] !== undefined) {
          const role = flags['role']
          if (typeof role !== 'string' || !INVITE_ROLES.includes(role)) {
            console.error(`Error: --role must be one of: ${INVITE_ROLES.join(', ')}`); process.exit(1)
          }
          body['role'] = role
        }
        const result = (await api.post(`${base}/invitations`, body)) as any
        analyticsEvent('member_invited', ws, { has_role: 'role' in body })
        if (isJSON) emitJSON(result)
        else console.log(`Invited ${email}${body['role'] ? ` as ${body['role']}` : ''} — id ${result?.id ?? '(pending)'}`)
        break
      }
      case 'role': {
        const memberId = positional[1]
        const role = positional[2]
        if (!memberId) { console.error('Error: member id required. Usage: driftless member role <memberId> <owner|admin|member>'); process.exit(1) }
        if (!role || !MEMBER_ROLES.includes(role)) {
          console.error(`Error: role must be one of: ${MEMBER_ROLES.join(', ')}`); process.exit(1)
        }
        const result = (await api.patch(`${base}/members/${encodeURIComponent(memberId)}`, { role })) as any
        analyticsEvent('member_role', ws, { role })
        if (isJSON) emitJSON(result)
        else console.log(`Role updated → ${result?.role ?? role} (member ${memberId})`)
        break
      }
      case 'rm': {
        const memberId = positional[1]
        if (!memberId) { console.error('Error: member id required. Usage: driftless member rm <memberId>'); process.exit(1) }
        const result = (await api.delete(`${base}/members/${encodeURIComponent(memberId)}`)) as any
        analyticsEvent('member_removed', ws, {})
        if (isJSON) emitJSON(result ?? { deleted: memberId })
        else console.log(`Member removed — id ${result?.deleted ?? memberId}`)
        break
      }
      case 'invites': {
        const result = (await api.get(`${base}/invitations`)) as any[]
        if (isJSON) { emitJSON(result); break }
        if (!Array.isArray(result) || !result.length) { console.log('No pending invitations.'); break }
        for (const inv of result) {
          const role = (inv.role || '?').toString().padEnd(8)
          console.log(`${inv.id}  ${role} ${inv.email ?? ''}  ${inv.created_at ?? ''}`)
        }
        break
      }
      default:
        console.error(`Unknown member subcommand: '${sub}'. Run 'driftless member --help'.`)
        process.exit(1)
    }
    await flushAndExit(0)
  } catch (e: any) {
    console.error(formatError(e))
    await flushAndExit(1)
  }
}
